import { timeRegex } from "./datetime.utils";

/**
 * Sorts rows chronologically by date first (if present), then by time.
 * Handles grouped rows with the same draggableId by sorting based on the date/time-containing row
 * and keeping grouped rows together. Also handles subrows structure.
 * Only sorts if the section has dynamicRows enabled.
 *
 * @param rows - Array of rows to sort
 * @param ascending - Whether to sort in ascending (true) or descending (false) order. Defaults to true.
 * @param isDynamicSection - Whether the section has dynamicRows enabled. If false, returns rows as-is.
 */
export const sortRowsByDateTime = (
  rows: any[],
  ascending: boolean = true,
  isDynamicSection: boolean = true,
): any[] => {
  // If not a dynamic section, return rows unchanged
  if (!isDynamicSection) {
    return rows;
  }
  // Helper function to extract date from a row
  const getDateFromRow = (row: any): Date | null => {
    // Handle both direct cols and subrows structure
    const colsToCheck = row.subrows ? row.subrows[0]?.cols : row.cols;

    if (!colsToCheck) return null;

    let sortByDate: Date | null = null;
    let fallbackDate: Date | null = null;

    // Look for date columns, prioritizing ones with sortby: true
    for (const col of Object.values(colsToCheck)) {
      const colData = col as any;

      // Check if this is a date column
      if (
        colData.unit === "date" &&
        colData.value &&
        typeof colData.value === "string"
      ) {
        try {
          const date = new Date(colData.value);

          // Prioritize date columns with sortby: true
          if (colData.sortby === true) {
            sortByDate = date;
          } else {
            fallbackDate = date;
          }
        } catch {
          continue;
        }
      }
    }

    // Return the sortby date if available, otherwise the fallback date
    return sortByDate || fallbackDate;
  };

  // Helper function to check if a row has a date field but empty value
  const hasEmptyDateField = (row: any): boolean => {
    // Handle both direct cols and subrows structure
    const colsToCheck = row.subrows ? row.subrows[0]?.cols : row.cols;

    if (!colsToCheck) return false;

    for (const col of Object.values(colsToCheck)) {
      const colData = col as any;

      // Check if this is a date column with no value or null/empty value
      if (colData.unit === "date") {
        if (!colData.value || colData.value === "" || colData.value === null) {
          return true;
        }

        // Also check for strings that can't be parsed as dates
        if (typeof colData.value === "string") {
          try {
            const date = new Date(colData.value);
            if (isNaN(date.getTime())) {
              return true; // Invalid date string
            }
          } catch {
            return true; // Can't parse as date
          }
        }
      }
    }

    return false;
  };

  // Helper to parse "HH:MM" or "HH:MM:SS" strings to total seconds.
  // Returns -1 for invalid / empty input.
  const parseTimeToSeconds = (timeString: string): number => {
    if (!timeString || !timeRegex.test(timeString)) return -1;
    const parts = timeString.split(":");
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parts[2] ? parseInt(parts[2], 10) : 0;
    if (hours > 23 || minutes > 59 || seconds > 59) return -1;
    return hours * 3600 + minutes * 60 + seconds;
  };

  // Helper function to extract time from a row's time picker value
  // Returns -1 for empty times, 0+ for valid times
  const getTimeFromRow = (row: any): number => {
    // Handle both direct cols and subrows structure
    const colsToCheck = row.subrows ? row.subrows[0]?.cols : row.cols;

    if (!colsToCheck) return -1; // No columns means empty time

    let sortByTime = -1; // Default to empty
    let fallbackTime = -1;

    // Look for time picker columns, prioritizing ones with sortby: true
    for (const col of Object.values(colsToCheck)) {
      const colData = col as any;

      if (
        colData.value &&
        typeof colData.value === "object" &&
        colData.value.time &&
        (colData.unit === "24lock" ||
          colData.unit === "time" ||
          colData.name?.toLowerCase().includes("time"))
      ) {
        const timeString = colData.value.time;
        if (typeof timeString === "string" && timeString !== "") {
          const numericTime = parseTimeToSeconds(timeString);
          if (numericTime !== -1) {
            // Prioritize time columns with sortby: true
            if (colData.sortby === true) {
              sortByTime = numericTime;
            } else if (fallbackTime === -1) {
              fallbackTime = numericTime;
            }
          }
        }
      }

      // Fallback: check for direct time string values
      if (
        colData.value &&
        typeof colData.value === "string" &&
        colData.value !== ""
      ) {
        const numericTime = parseTimeToSeconds(colData.value);
        if (numericTime !== -1) {
          // Prioritize time columns with sortby: true
          if (colData.sortby === true) {
            sortByTime = numericTime;
          } else if (fallbackTime === -1) {
            fallbackTime = numericTime;
          }
        }
      }
    }

    // Return the sortby time if available, otherwise the fallback time
    return sortByTime !== -1 ? sortByTime : fallbackTime;
  };

  // Helper function to create a sortable key combining date and time
  const getSortKey = (
    row: any,
  ): {
    date: Date | null;
    time: number;
    sortValue: number;
    isEmpty: boolean;
    emptyDatePriority: number;
  } => {
    const date = getDateFromRow(row);
    const time = getTimeFromRow(row);
    const isEmpty = time === -1; // Check if time is empty
    const hasEmptyDate = hasEmptyDateField(row);

    // Create a numeric sort value
    let sortValue: number;

    // Special flag to ensure empty date fields are always at the bottom
    // regardless of sort direction
    let emptyDatePriority = 0;

    if (date && !isNaN(date.getTime()) && !hasEmptyDate) {
      // Has valid date - highest priority
      const baseDateValue =
        Math.floor(date.getTime() / (1000 * 60 * 60 * 24)) * 86400;

      if (isEmpty) {
        // Valid date but empty time - always put at bottom for that date
        // For ascending: use 86399 (23:59:59) to put at bottom
        // For descending: use 0 (00:00:00) to put at bottom when reversed
        const timeOffset = ascending ? 86399 : 0;
        sortValue = baseDateValue + timeOffset;
      } else {
        // Has both date and time - normal chronological sorting
        // time is already in total seconds
        sortValue = baseDateValue + time;
      }

      // These rows have valid dates - top priority group
      emptyDatePriority = 0;
    } else {
      // Either has empty date field or no date field at all - these go at bottom
      // Flag these for special handling in the sort comparator
      emptyDatePriority = 1;

      // Still calculate a sort value for use within the bottom group
      if (isEmpty) {
        // No time: absolute bottom within the bottom group
        sortValue = 500000;
      } else {
        // Has time: sort by time
        sortValue = time;
      }
    }

    // Note: We don't negate sortValue here based on ascending flag
    // The sort direction is handled in the comparison functions

    return { date, time, sortValue, isEmpty, emptyDatePriority };
  };

  // Group rows by draggableId (if they have one)
  const groupedRows = new Map<string, any[]>();
  const ungroupedRows: any[] = [];

  rows.forEach(row => {
    const draggableId = row.draggableId;

    if (draggableId) {
      if (!groupedRows.has(draggableId)) {
        groupedRows.set(draggableId, []);
      }
      groupedRows.get(draggableId)!.push(row);
    } else {
      ungroupedRows.push(row);
    }
  });

  // For each group, find the row with date/time and use that for sorting
  const groupsWithSortKey: Array<{ sortValue: number; rows: any[] }> = [];

  groupedRows.forEach(groupRows => {
    let sortValueForGroup = ascending ? 10000000 : -10000000; // Default to bottom regardless of sort direction

    // Find the first row in the group that has a date/time value
    for (const row of groupRows) {
      const sortKey = getSortKey(row);
      if (sortKey.date || sortKey.time > 0) {
        sortValueForGroup = sortKey.sortValue;
        break;
      }
    }

    groupsWithSortKey.push({
      sortValue: sortValueForGroup,
      rows: groupRows,
    });
  });

  // Sort ungrouped rows individually
  const sortedUngroupedRows = ungroupedRows; // Don't pre-sort, will be sorted in final step

  // Sort groups by their sort key
  const sortedGroups = groupsWithSortKey; // Don't pre-sort, will be sorted in final step

  // Combine all rows: sorted groups + sorted ungrouped rows, but mix them chronologically
  const allSortableItems: Array<{
    sortValue: number;
    isGroup: boolean;
    data: any;
  }> = [];

  // Add groups as sortable items
  sortedGroups.forEach(group => {
    allSortableItems.push({
      sortValue: group.sortValue,
      isGroup: true,
      data: group.rows,
    });
  });

  // Add individual rows as sortable items
  sortedUngroupedRows.forEach(row => {
    const sortKey = getSortKey(row);
    allSortableItems.push({
      sortValue: sortKey.sortValue,
      isGroup: false,
      data: row,
    });
  });

  // Sort all items by sort value with comprehensive comparison
  allSortableItems.sort((a, b) => {
    // Get the empty date priority for each item
    let emptyDatePriorityA = 0;
    let emptyDatePriorityB = 0;

    if (a.isGroup) {
      // For groups, check the rows in the group to find if any have empty dates
      for (const row of a.data) {
        const sortKey = getSortKey(row);
        if (sortKey.emptyDatePriority === 1) {
          emptyDatePriorityA = 1;
          break;
        }
      }
    } else {
      // For individual rows, get the sortKey directly
      const sortKey = getSortKey(a.data);
      emptyDatePriorityA = sortKey.emptyDatePriority;
    }

    if (b.isGroup) {
      for (const row of b.data) {
        const sortKey = getSortKey(row);
        if (sortKey.emptyDatePriority === 1) {
          emptyDatePriorityB = 1;
          break;
        }
      }
    } else {
      const sortKey = getSortKey(b.data);
      emptyDatePriorityB = sortKey.emptyDatePriority;
    }

    // First compare by empty date priority (always keeps empty dates at bottom)
    if (emptyDatePriorityA !== emptyDatePriorityB) {
      return emptyDatePriorityA - emptyDatePriorityB;
    }

    // Then sort by sort value within the same priority group
    const diff = a.sortValue - b.sortValue;
    return ascending ? diff : -diff;
  });

  // Build final result
  const result: any[] = [];
  allSortableItems.forEach(item => {
    if (item.isGroup) {
      result.push(...item.data);
    } else {
      result.push(item.data);
    }
  });

  return result;
};
